<template>
  <el-drawer
      :visible.sync="internalVisible"
      title="地址管理"
      size="50%"
      :wrapperClosable="false"
      @close="handleClose"
      class="address-drawer"
  >
    <div class="address-container">
      <div class="address-header">
        <h3 class="address-title">客户收货地址</h3>
        <el-button
            size="small"
            type="primary"
            icon="el-icon-plus"
            @click="toSave({})"
            class="add-btn"
        >
          新增地址
        </el-button>
      </div>

      <div class="address-list">
        <el-scrollbar style="height: 100%">
          <!-- 按行分组渲染 -->
          <div v-for="(group, rowIndex) in groupedAddresses" :key="'row_' + rowIndex" class="address-row">
            <el-row :gutter="12" type="flex" justify="start">
              <el-col :span="12" v-for="item in group" :key="item.id">
                <div
                    class="address-card"
                    :class="{ 'default-address': item.type === '常用' }"
                >
                  <div class="card-content">
                    <div class="address-icon">
                      <i class="el-icon-location-outline"></i>
                    </div>
                    <div class="address-info">
                      <div class="address-top">
                        <span class="contact-name">{{ item.name }}</span>
                        <span class="contact-phone">{{ item.telNum }}</span>
                        <el-tag v-if="item.type === '常用'" size="mini" type="primary" class="default-tag">常用</el-tag>
                      </div>
                      <div class="address-region">
                        {{ item.addressCode ? item.addressCode.join(' ') : '' }}
                      </div>
                      <div class="address-detail">
                        {{ item.address }}
                      </div>
                      <div class="address-footer">
                        <span class="operator">创建人: {{ item.operatorName || '系统' }}</span>
                        <span class="create-time">{{ item.createTime }}</span>
                      </div>
                    </div>
                    <div class="address-actions">
                      <el-button
                          size="mini"
                          type="text"
                          icon="el-icon-edit"
                          @click.stop="toSave(item)"
                          class="action-btn edit-btn"
                          :loading="loadingMap[`edit_${item.id}`]"
                      >
                        编辑
                      </el-button>
                      <el-button
                          size="mini"
                          type="text"
                          icon="el-icon-delete"
                          @click.stop="toDelete(item)"
                          class="action-btn delete-btn"
                          :loading="loadingMap[`delete_${item.id}`]"
                      >
                        删除
                      </el-button>
                      <el-button
                          v-if="item.type !== '常用'"
                          size="mini"
                          type="text"
                          @click.stop="setDefault(item)"
                          class="action-btn default-btn"
                          :loading="loadingMap[`default_${item.id}`]"
                      >
                        设为常用
                      </el-button>
                    </div>
                  </div>
                </div>
              </el-col>

              <!-- 占位卡片，当本组只有1个元素时补一个隐藏卡片占位 -->
              <el-col v-if="group.length === 1" :span="12">
                <div class="address-card address-card--placeholder"></div>
              </el-col>
            </el-row>
          </div>

          <el-empty
              v-if="tableData.length === 0"
              description="暂无收货地址"
              class="empty-address"
          >
            <el-button
                type="primary"
                size="small"
                @click="toSave({})"
            >
              添加第一个地址
            </el-button>
          </el-empty>
        </el-scrollbar>
      </div>
    </div>

    <!-- 编辑抽屉 -->
    <el-drawer
        :title="saveForm.id ? '编辑地址' : '新增地址'"
        :visible.sync="editDrawerVisible"
        size="40%"
        :wrapperClosable="false"
        @close="editDrawerVisible = false"
        class="edit-drawer"
        :append-to-body="true"
    >
      <div class="edit-container">
        <el-form
            ref="saveForm"
            :model="saveForm"
            :rules="rules"
            label-width="100px"
            class="address-form"
        >
          <el-form-item label="收货人姓名:" prop="name">
            <el-input
                v-model="saveForm.name"
                placeholder="请输入收货人姓名"
                size="small"
            />
          </el-form-item>
          <el-form-item label="手机号码:" prop="telNum">
            <el-input
                v-model="saveForm.telNum"
                placeholder="请输入手机号码"
                size="small"
                maxlength="11"
                show-word-limit
            />
          </el-form-item>
          <el-form-item label="所在地区:" prop="addressCode">
            <cascader
                v-model="saveForm.addressCode"
                placeholder="请选择省市区"
            />
          </el-form-item>
          <el-form-item label="详细地址:" prop="address">
            <el-input
                v-model="saveForm.address"
                placeholder="请输入详细地址"
                size="small"
            />
          </el-form-item>
        </el-form>

        <div class="form-actions">
          <el-button @click="editDrawerVisible = false" class="cancel-btn">取消</el-button>
          <el-button
              type="primary"
              @click="save"
              class="save-btn"
              :loading="saveLoading"
          >
            保存地址
          </el-button>
        </div>
      </div>
    </el-drawer>
  </el-drawer>
</template>

<script>
import cascader from "../utils/cascader.vue";

export default {
  name: "CustomerAddress",
  components: { cascader },
  props: {
    visible: Boolean,
    customerId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      internalVisible: false,
      tableData: [],
      saveForm: {
        isDefault: false,
        type: '普通',
      },
      editDrawerVisible: false,
      saveLoading: false,
      loadingMap: {},
      rules: {
        name: [
          { required: true, message: "姓名不能为空", trigger: "blur" },
          { min: 2, max: 10, message: "长度在2到10个字符", trigger: "blur" }
        ],
        telNum: [
          { required: true, message: "联系方式不能为空", trigger: "blur" },
          { pattern: /^1[3-9]\d{9}$/, message: "请输入正确的手机号码", trigger: "blur" }
        ],
        addressCode: [
          { required: true, message: "请选择所在地区", trigger: "change" }
        ],
        address: [
          { required: true, message: "详细地址不能为空", trigger: "blur" },
          { min: 5, max: 100, message: "长度在5到100个字符", trigger: "blur" }
        ],
      },
    };
  },
  computed: {
    sortedAddresses() {
      return [...this.tableData].sort((a, b) => b.isDefault - a.isDefault);
    },
    groupedAddresses() {
      const groups = [];
      for (let i = 0; i < this.sortedAddresses.length; i += 2) {
        groups.push(this.sortedAddresses.slice(i, i + 2));
      }
      return groups;
    }
  },
  watch: {
    visible(val) {
      this.internalVisible = val;
      if (val) {
        this.getList();
      }
    },
    internalVisible(val) {
      if (!val) {
        this.$emit("update:visible", false);
      }
    },
  },
  methods: {
    handleClose() {
      this.internalVisible = false;
    },
    getList() {
      this.$http.get('/customerAddress/addressList', {
        params: { customerId: this.customerId }
      }).then(res => {
        if (res.data.success) {
          this.tableData = (res.data.resultObj || []).map(item => {
            return {
              ...item,
              addressCode: item.addressCode ? item.addressCode.split(',') : []
            };
          });
        } else {
          this.$message.error(res.data.message || '获取地址失败');
        }
      }).catch(err => {
        console.error(err);
        this.$message.error('网络错误，无法获取地址');
      });
    },
    toSave(data) {
      this.saveForm = JSON.parse(JSON.stringify(data));
      this.editDrawerVisible = true;
      this.$nextTick(() => {
        this.$refs.saveForm && this.$refs.saveForm.clearValidate();
      });
    },
    save() {
      this.$refs.saveForm.validate((valid) => {
        if (!valid) return;

        this.saveLoading = true;
        const url = this.saveForm.id ? '/customerAddress/addressUpdate' : '/customerAddress/addressInsert';
        const method = 'post';

        const data = {
          ...this.saveForm,
          customerId: this.customerId,
          loginId: JSON.parse(localStorage.getItem('logininfo')).id,
          addressCode: Array.isArray(this.saveForm.addressCode)
              ? this.saveForm.addressCode.join(',')
              : this.saveForm.addressCode
        };

        this.$http[method](url, data)
            .then(res => {
              if (res.data.success) {
                this.$message.success(res.data.message || '保存成功');
                this.editDrawerVisible = false;
                this.getList();
              } else {
                this.$message.error(res.data.message || '保存失败');
              }
            })
            .catch(err => {
              console.error(err);
              this.$message.error('网络错误，保存失败');
            })
            .finally(() => {
              this.saveLoading = false;
            });
      });
    },
    toDelete(row) {
      this.$confirm("确定删除该地址吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
        customClass: "delete-confirm",
        appendToBody: true
      }).then(() => {
        this.$set(this.loadingMap, `delete_${row.id}`, true);
        this.$http.delete('/customerAddress/addressDelete', {
          params: { id: row.id }
        }).then(res => {
          if (res.data.success) {
            this.$message.success(res.data.message || '删除成功');
            this.getList();
          } else {
            this.$message.error(res.data.message || '删除失败');
          }
        }).catch(err => {
          console.error(err);
          this.$message.error('网络错误，删除失败');
        }).finally(() => {
          this.$set(this.loadingMap, `delete_${row.id}`, false);
        });
      }).catch(() => {});
    },
    setDefault(item) {
      this.$set(this.loadingMap, `default_${item.id}`, true);
      this.$http.put('/customerAddress/setDefault', null, { params: { id: item.id } })
          .then(res => {
            if (res.data.success) {
              this.$message.success('默认地址设置成功');
              this.getList();
            } else {
              this.$message.error(res.data.message || '设置默认地址失败');
            }
          })
          .catch(() => {
            this.$message.error('网络错误，设置失败');
          })
          .finally(() => {
            this.$set(this.loadingMap, `default_${item.id}`, false);
          });
    }
  }
};
</script>

<style lang="scss" scoped>
.address-drawer {
  /deep/ .el-drawer__header {
    margin-bottom: 0;
    padding: 16px 20px;
    border-bottom: 1px solid #f0f0f0;
    color: #333;
    font-size: 16px;
    font-weight: 500;
  }

  /deep/ .el-drawer__body {
    padding: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    background-color: #f5f7fa;
  }
}

.address-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.address-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

  .address-title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: #333;
  }

  .add-btn {
    padding: 6px 12px;
    border-radius: 4px;
    font-weight: 500;
    font-size: 12px;
  }
}

.address-list {
  flex: 1;
  padding: 12px;
  overflow: hidden;
  background-color: #f5f7fa;

  /deep/ .el-scrollbar__wrap {
    overflow-x: hidden;
  }
}

.address-row {
  margin-bottom: 12px;
}

.address-card {
  margin-top: 5px;
  margin-bottom: 12px;
  padding: 12px;
  background-color: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  border: 1px solid #eaeaea;
  position: relative;
  height: 100%;
  box-sizing: border-box;

  &:hover {
    border-color: #409EFF;
    box-shadow: 0 2px 8px 0 rgba(64, 158, 255, 0.15);
    transform: translateY(-1px);

    .address-actions {
      opacity: 1;
      transform: translateX(0);
    }
  }

  &.default-address {
    background-color: #f0f7ff;
    border-color: #cce0ff;

    .address-icon {
      color: #409EFF;
    }

    &:hover {
      border-color: #409EFF;
      box-shadow: 0 2px 8px 0 rgba(64, 158, 255, 0.15);
    }
  }
}

.address-card--placeholder {
  visibility: hidden;
  height: 100%;
  margin-bottom: 12px;
  border-radius: 6px;
}

.card-content {
  display: flex;
  align-items: flex-start;
  height: 100%;
}

.address-icon {
  margin-right: 10px;
  color: #909399;
  font-size: 20px;
  margin-top: 2px;
  flex-shrink: 0;
}

.address-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.address-top {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  font-weight: 600;
  color: #303133;

  .contact-name {
    margin-right: 12px;
  }

  .contact-phone {
    color: #606266;
    font-weight: 400;
  }

  .default-tag {
    margin-left: auto;
  }
}

.address-region {
  color: #606266;
  font-size: 13px;
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.address-detail {
  color: #909399;
  font-size: 13px;
  line-height: 1.4;
  max-height: 2.8em;
  overflow: hidden;
  margin-bottom: 8px;
  word-break: break-word;
}

.address-footer {
  font-size: 12px;
  color: #c0c4cc;
  display: flex;
  justify-content: space-between;
}

.address-actions {
  position: absolute;
  right: 12px;
  bottom: 12px;
  opacity: 0;
  transition: all 0.3s ease;
  transform: translateX(10px);

  .action-btn {
    margin-left: 8px;
    font-size: 12px;
    color: #409EFF;
    cursor: pointer;
  }
}

.empty-address {
  margin-top: 40px;
  text-align: center;
}
.edit-container {
  padding: 20px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;

  .cancel-btn {
    margin-right: 10px;
  }
}

.address-form {
  max-width: 100%;
}

</style>



// WEBPACK FOOTER //
// CustomerAddress.vue?6ab3a00c